/**
 * OutletReferralsCard — admin tile for marketing/outreach attribution.
 *
 * Lets the admin create short ?ref=<code> tags, copy ready-to-paste
 * tracking URLs, and watch visits + signups + conversion% roll in per
 * outlet (NeuroClastic, ADDitude, podcast appearances, etc).
 *
 * Distinct from "Invite a Friend! Analytics" (user-to-user credits)
 * which lives in the same Referrals tab.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '@/hooks/useFetch';
import { toast } from 'sonner';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Megaphone, Plus, Copy, Trash2, Link2, ArrowUpDown, TrendingUp } from 'lucide-react';
import Sparkline from '@/components/admin/Sparkline';

const APEX = 'https://clearcommunicationapp.com';

const SORT_OPTIONS = [
  { value: 'visits-desc',     label: 'Traffic — most visits' },
  { value: 'visits-asc',      label: 'Traffic — fewest visits' },
  { value: 'conversion-desc', label: 'Conversion % — highest' },
  { value: 'conversion-asc',  label: 'Conversion % — lowest' },
  { value: 'signups-desc',    label: 'Signups — most' },
  { value: 'label-asc',       label: 'Name — A → Z' },
  { value: 'label-desc',      label: 'Name — Z → A' },
  { value: 'created-desc',    label: 'Newest first' },
  { value: 'created-asc',     label: 'Oldest first' },
];

function applySort(items, key) {
  // Always return a copy — never mutate state in place.
  const arr = [...items];
  switch (key) {
    case 'visits-desc':     arr.sort((a, b) => (b.visits ?? 0) - (a.visits ?? 0)); break;
    case 'visits-asc':      arr.sort((a, b) => (a.visits ?? 0) - (b.visits ?? 0)); break;
    case 'conversion-desc': arr.sort((a, b) => (b.conversion_rate ?? 0) - (a.conversion_rate ?? 0)); break;
    case 'conversion-asc':  arr.sort((a, b) => (a.conversion_rate ?? 0) - (b.conversion_rate ?? 0)); break;
    case 'signups-desc':    arr.sort((a, b) => (b.signups ?? 0) - (a.signups ?? 0)); break;
    case 'label-asc':       arr.sort((a, b) => (a.label || '').localeCompare(b.label || '')); break;
    case 'label-desc':      arr.sort((a, b) => (b.label || '').localeCompare(a.label || '')); break;
    case 'created-desc':    arr.sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || ''))); break;
    case 'created-asc':     arr.sort((a, b) => String(a.created_at || '').localeCompare(String(b.created_at || ''))); break;
    default: break;
  }
  return arr;
}

export function OutletReferralsCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ code: '', label: '', notes: '' });
  // Cache 30-day trend data per outlet code so toggling the trend
  // panel open / closed doesn't re-fetch every time.
  const [trends, setTrends] = useState({});  // code → {days: [...], summary: {...}}
  const [expandedCode, setExpandedCode] = useState(null);
  const [trendBusy, setTrendBusy] = useState(null);
  // Default sort = "Traffic — most visits" matches the backend's default
  // order so users see the same list shape they expect from the API.
  // Persist the user's choice across sessions so each admin lands on
  // the lens that's most useful to THEM (e.g. once you have 5+ outlets,
  // most users want "Conversion % — highest" pinned).
  const [sortKey, setSortKey] = useState(() => {
    try { return localStorage.getItem('cca_outlet_sort') || 'visits-desc'; }
    catch { return 'visits-desc'; }
  });

  const sorted = useMemo(() => applySort(items, sortKey), [items, sortKey]);

  const onSortChange = (next) => {
    setSortKey(next);
    try { localStorage.setItem('cca_outlet_sort', next); } catch { /* ignore */ }
  };

  const load = async () => {
    setLoading(true);
    const { data, error } = await apiFetch('/api/admin/outlet-referrals');
    if (error) {
      toast.error(`Could not load outlet referrals: ${error}`);
    } else {
      setItems(data?.items || []);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggleTrend = async (code) => {
    if (expandedCode === code) {
      setExpandedCode(null);
      return;
    }
    setExpandedCode(code);
    if (!trends[code]) {
      setTrendBusy(code);
      const { data, error } = await apiFetch(`/api/admin/outlet-referrals/${encodeURIComponent(code)}/trend?days=30`);
      setTrendBusy(null);
      if (error) {
        toast.error(`Trend load failed: ${error}`);
        return;
      }
      setTrends((t) => ({ ...t, [code]: data }));
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const code = form.code.trim().toLowerCase();
    const label = form.label.trim();
    if (!code || !label) {
      toast.error('Code and label are required.');
      return;
    }
    if (!/^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/.test(code)) {
      toast.error('Code must be lowercase letters, digits, or hyphens (2-40 chars).');
      return;
    }
    setCreating(true);
    const { data, error, status } = await apiFetch('/api/admin/outlet-referrals', {
      method: 'POST',
      body: { code, label, notes: form.notes.trim() || null },
    });
    setCreating(false);
    if (error) {
      const msg = status === 409 ? `Code "${code}" already exists.` : error;
      toast.error(msg);
      return;
    }
    toast.success(`Outlet "${data.outlet.label}" created.`);
    setForm({ code: '', label: '', notes: '' });
    await load();
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Delete outlet "${code}"? Past visits + attributions stay in the database for historical reporting.`)) {
      return;
    }
    const { error } = await apiFetch(`/api/admin/outlet-referrals/${encodeURIComponent(code)}`, {
      method: 'DELETE',
    });
    if (error) {
      toast.error(`Could not delete: ${error}`);
      return;
    }
    toast.success(`Outlet "${code}" deleted.`);
    await load();
  };

  const copyUrl = (code) => {
    const url = `${APEX}/?ref=${encodeURIComponent(code)}`;
    try {
      navigator.clipboard.writeText(url);
      toast.success(`Copied: ${url}`);
    } catch {
      toast.error('Could not copy — please copy manually.');
    }
  };

  // UTM-source alias: some email gateways (Substack, Mailchimp, Beehiiv)
  // auto-strip non-`utm_*` query params. Copying this variant ensures the
  // ref still attributes even after those gateways rewrite the link.
  const copyUtmUrl = (code) => {
    const url = `${APEX}/?utm_source=${encodeURIComponent(code)}`;
    try {
      navigator.clipboard.writeText(url);
      toast.success(`Copied UTM variant: ${url}`);
    } catch {
      toast.error('Could not copy — please copy manually.');
    }
  };

  return (
    <Card className="border-[#00E5CC]/30" data-testid="outlet-referrals-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Megaphone className="h-5 w-5 text-[#00E5CC]" />
          Outlet Referrals — Marketing &amp; Press Tracking
        </CardTitle>
        <CardDescription>
          Create a short code for every newsletter, podcast, or partner you
          pitch. Each code generates a tracking URL like{' '}
          <code className="bg-muted px-1.5 py-0.5 rounded">
            {APEX}/?ref=neuroclastic
          </code>{' '}
          — visits and signups roll up here so you can see which placements
          actually convert. First-touch attribution.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Create form */}
        <form
          onSubmit={handleCreate}
          className="grid gap-3 p-4 rounded-lg border bg-muted/30"
          data-testid="outlet-referrals-create-form"
        >
          <h4 className="text-sm font-semibold flex items-center gap-2">
            <Plus className="h-4 w-4" /> Create new outlet code
          </h4>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="outlet-code" className="text-xs">Code (lowercase, no spaces)</Label>
              <Input
                id="outlet-code"
                data-testid="outlet-referrals-code-input"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="neuroclastic"
                maxLength={40}
              />
            </div>
            <div>
              <Label htmlFor="outlet-label" className="text-xs">Display label</Label>
              <Input
                id="outlet-label"
                data-testid="outlet-referrals-label-input"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="NeuroClastic newsletter"
                maxLength={120}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="outlet-notes" className="text-xs">Notes (optional)</Label>
            <Textarea
              id="outlet-notes"
              data-testid="outlet-referrals-notes-input"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Pitched March 12 — Sarah replied, scheduled for April 7 issue."
              rows={2}
              maxLength={500}
            />
          </div>
          <div>
            <Button
              type="submit"
              disabled={creating}
              data-testid="outlet-referrals-submit-btn"
              className="bg-[#00E5CC] text-[#0d1f2d] hover:bg-[#00D9C0]"
            >
              {creating ? 'Creating…' : 'Create outlet code'}
            </Button>
          </div>
        </form>

        {/* Stats table */}
        <div>
          <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <Link2 className="h-4 w-4" /> Active outlets ({items.length})
            </h4>
            {items.length > 0 && (
              <div className="flex items-center gap-2">
                <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" />
                <Label htmlFor="outlet-sort" className="sr-only">Sort outlets</Label>
                <Select value={sortKey} onValueChange={onSortChange}>
                  <SelectTrigger
                    id="outlet-sort"
                    className="h-8 w-[220px] text-xs"
                    data-testid="outlet-referrals-sort"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SORT_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value} data-testid={`outlet-sort-opt-${o.value}`}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          {loading ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center border rounded-lg">
              No outlet codes yet. Create one above to start tracking.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="outlet-referrals-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Outlet</TableHead>
                    <TableHead className="text-right">Visits</TableHead>
                    <TableHead className="text-right">Signups</TableHead>
                    <TableHead className="text-right">VIP</TableHead>
                    <TableHead className="text-right">Conv %</TableHead>
                    <TableHead className="text-right w-[160px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.map((o) => (
                    <React.Fragment key={o.code}>
                    <TableRow data-testid={`outlet-row-${o.code}`}>
                      <TableCell>
                        <div className="font-medium">{o.label}</div>
                        <div className="text-xs text-muted-foreground">
                          <code>?ref={o.code}</code>
                        </div>
                        {o.notes && (
                          <div className="text-xs text-muted-foreground mt-1 italic max-w-xs">
                            {o.notes}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono">{o.visits}</TableCell>
                      <TableCell className="text-right font-mono">{o.signups}</TableCell>
                      <TableCell className="text-right">
                        {o.vip_conversions > 0 ? (
                          <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30">
                            {o.vip_conversions}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {o.conversion_rate > 0
                          ? <span className="text-green-600 font-semibold">{o.conversion_rate}%</span>
                          : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-1 justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggleTrend(o.code)}
                            data-testid={`outlet-trend-${o.code}`}
                            aria-label="Show 30-day trend"
                            title="30-day visit/signup trend"
                            className={expandedCode === o.code ? 'bg-[#00E5CC]/15 text-[#00A896] border-[#00A896]/40' : ''}
                          >
                            <TrendingUp className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyUrl(o.code)}
                            data-testid={`outlet-copy-${o.code}`}
                            aria-label="Copy ?ref= tracking URL"
                            title={`Copy ${APEX}/?ref=${o.code}`}
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyUtmUrl(o.code)}
                            data-testid={`outlet-copy-utm-${o.code}`}
                            aria-label="Copy ?utm_source= tracking URL"
                            title={`Copy ${APEX}/?utm_source=${o.code} — use for email platforms that strip non-utm params`}
                            className="text-xs font-mono px-1.5"
                          >
                            UTM
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(o.code)}
                            data-testid={`outlet-delete-${o.code}`}
                            aria-label="Delete outlet"
                            className="text-red-500 hover:bg-red-500/10"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {expandedCode === o.code && (
                      <TableRow
                        className="bg-muted/20 hover:bg-muted/20"
                        data-testid={`outlet-trend-row-${o.code}`}
                      >
                        <TableCell colSpan={6} className="py-3">
                          {trendBusy === o.code ? (
                            <p className="text-xs text-muted-foreground text-center">Loading 30-day trend…</p>
                          ) : trends[o.code] ? (
                            <div className="flex items-center gap-4 flex-wrap">
                              <div>
                                <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Visits — last 30d</p>
                                <div className="flex items-baseline gap-2 mt-0.5">
                                  <span className="font-mono text-lg font-bold text-[#00A896]">
                                    {trends[o.code].summary.total_visits}
                                  </span>
                                  <Sparkline
                                    values={trends[o.code].days.map((d) => d.visits)}
                                    width={180}
                                    height={28}
                                    testid={`outlet-trend-visits-${o.code}`}
                                  />
                                </div>
                              </div>
                              <div>
                                <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Signups — last 30d</p>
                                <div className="flex items-baseline gap-2 mt-0.5">
                                  <span className="font-mono text-lg font-bold text-emerald-600">
                                    {trends[o.code].summary.total_signups}
                                  </span>
                                  <Sparkline
                                    values={trends[o.code].days.map((d) => d.signups)}
                                    width={180}
                                    height={28}
                                    testid={`outlet-trend-signups-${o.code}`}
                                  />
                                </div>
                              </div>
                              <div>
                                <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">30d Conv %</p>
                                <p className="font-mono text-lg font-bold text-green-600 mt-0.5">
                                  {trends[o.code].summary.conversion_rate}%
                                </p>
                              </div>
                            </div>
                          ) : (
                            <p className="text-xs text-muted-foreground text-center">No trend data.</p>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          Tip: visits are counted anonymously the moment someone lands on a
          <code className="mx-1">?ref=</code>URL. Signups are credited via
          <strong> first-touch attribution</strong> when that visitor later
          creates an account — even if days later and across devices in the
          same browser. Deleting an outlet preserves past attribution data
          (the row simply disappears from this list).
        </p>
      </CardContent>
    </Card>
  );
}

export default OutletReferralsCard;
